import { useState } from "react";
import { useI18n } from "i18n";
import { selectCurrentEventId } from "modules/event/selectors";
import { useSelector } from "react-redux";
import { useAppDispatch } from "store/hooks";
import { onSnapshot, query, orderBy, updateDoc, doc } from "firebase/firestore";
import logger from "logging/logger";
import { admitAllLobbyGuests } from "services/apiService/apis";
import useNotificationActions from "../../notification/hooks/useNotificationActions";
import { removeGuestFromLobby, updateGuestsToLobby } from "../redux/slice";
import { getLobbyDoc } from "../utils/lobbyFirestoreUtils";
import { selectSortedWaitingGuests } from "../redux/selectors";
import { ILobbyGuest } from "../types";

const useLobbyHostFirestore = () => {
  const { t } = useI18n(["errorPage", "server", "event"]);
  const waitingGuests = useSelector(selectSortedWaitingGuests);
  const dispatch = useAppDispatch();
  const { addErrorNotification } = useNotificationActions();
  const currentEventId = useSelector(selectCurrentEventId);
  const [isLoading, setIsLoading] = useState(false);

  const admitAllGuestIntoEvent = async (eventId: string) => {
    logger.info(`[admitAllGuestIntoEvent] lobby for event: ${eventId}`);

    const response = await admitAllLobbyGuests(eventId);

    logger.info(`[admitAllGuestIntoEvent] response: ${response}`);

    return response;
  };

  const subscribeToLobby = (eventId: string) =>
    onSnapshot(query(getLobbyDoc(eventId), orderBy("time")), (snap) => {
      const guests = [] as unknown as ILobbyGuest[];

      snap.docChanges().forEach((change) => {
        if (change.type === "removed") {
          dispatch(removeGuestFromLobby(change.doc.id));

          return;
        }
        const data = {
          ...change.doc.data(),
          time: change.doc.data().time.toMillis(),
        } as ILobbyGuest;

        guests.push({ ...data, userId: change.doc.id });
      });
      dispatch(updateGuestsToLobby(guests));
    });

  const admitGuestIntoEvent = async (
    eventId: string,
    userId: string,
  ): Promise<string> =>
    new Promise((resolve) => {
      logger.info(
        `[admitGuestIntoEvent] for event: ${eventId} user: ${userId}`,
      );

      updateDoc(doc(getLobbyDoc(eventId), userId), { state: "APPROVED" }).catch(
        (err) => {
          logger.error(
            `[admitGuestIntoEvent] err: ${err.message} user: ${userId}, ev: ${eventId}`,
          );
          resolve(err.message);
        },
      );
    });

  const admitAllGuests = () => {
    setIsLoading(true);
    const userIds = waitingGuests.map((guest) => guest.userId);

    if (!userIds?.length || !currentEventId) {
      setIsLoading(false);
      return Promise.resolve();
    }

    return admitAllGuestIntoEvent(currentEventId)
      .then((response) => {
        if (!response?.isSuccess) {
          addErrorNotification({
            message: response?.message
              ? t(response?.message)
              : t("errorPage:admit.all.failed"),
          });
        }

        return response;
      })
      .catch((err) => {
        addErrorNotification({
          message: err.message,
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return {
    isLoading,
    admitGuestIntoEvent,
    subscribeToLobby,
    admitAllGuests,
  };
};

export default useLobbyHostFirestore;
